import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { CounterpartyCombobox } from '@/features/counterparties/CounterpartyCombobox';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  count: number;
  onConfirm: (counterpartyId: string | null) => Promise<void>;
}

export function BulkCounterpartyDialog({ open, onOpenChange, count, onConfirm }: Props) {
  const [selected, setSelected] = useState<string | undefined>();
  const [clearMode, setClearMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleApply = async () => {
    setSubmitting(true);
    try {
      await onConfirm(clearMode ? null : (selected ?? null));
      onOpenChange(false);
      setSelected(undefined);
      setClearMode(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!submitting) onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-brand text-base">Contraparte em lote</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">{count} transação(ões) selecionada(s).</p>
          <div className="space-y-1.5">
            <Label>Contraparte</Label>
            <CounterpartyCombobox
              value={clearMode ? undefined : selected}
              onChange={(id) => {
                setClearMode(false);
                setSelected(id);
              }}
              role="any"
              placeholder="Selecione um fornecedor ou cliente…"
              disabled={clearMode}
            />
            <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={clearMode}
                onChange={(e) => setClearMode(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Remover contraparte das transações selecionadas
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            disabled={submitting || (!clearMode && !selected)}
            className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Aplicando…
              </>
            ) : (
              'Aplicar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
